import React, { useEffect, useRef, useState, useCallback, FC, useMemo } from "react";
import { ActionCreatorsMapObject } from "redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { ExclamationCircleFilled } from "src/icons";
import { IAction } from "src/store";
import Shimmer from "src/shared/components/CustomisedShimmer";
import { safeGet } from "src/shared/utils";
import styles from "./index.module.scss";
import AreaChartMini from "src/shared/components/AreaChart";
import { ISinglePin } from "src/shared/types";
import { PencilIcon } from "src/assets/pencil-icon.js";
import ChartFallback from "src/shared/components/ChartFallback";
import { getTimeRoundOffData } from "src/shared/utils/timeUtils";
import { GRANULARITY_MAPPING, RunningAnobisMode } from "src/shared/config";
import { IsTabHidden } from "src/shared/utils/customTab";
import { Button, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { devLogging } from "src/utils/logger";
import { parseBigNumber } from "src/utils/parseNumber";
import { getChartNumberStyle } from "src/utils/sortMetricsByDelta";

interface IDispatchProps extends ActionCreatorsMapObject {
  fetchChart: (sliceId: number) => IAction<string | symbol, any>;
}

interface IOverviewCardProps {
  data: ISinglePin;

  showDetails(id: any, name: string): void;

  id: number;
  failure: boolean;
  pending: boolean;
  selectedTab: string;
  brokenPodsVisibleTabs?: string[];
}

export type WDYR = {
  whyDidYouRender?: boolean;
};

const flattenFilters = (filterObj) => {
  const reducer = (accumulator, currentValue) => {
    const a = accumulator.concat(...currentValue.value);
    return a;
  };
  return filterObj.reduce(reducer, []).join(" | ");
};

const OverviewCard: FC<IOverviewCardProps & RouteComponentProps> & WDYR = (props) => {
  const navigateToTakeNotes = useCallback(
    (evt) => {
      evt.stopPropagation();
      props.history.push(`${props.id}/takeNotes`);
    },
    [props.id]
  );
  const isChartFetched = typeof safeGet(props, "data.currentChart") === "number" ? false : true;

  const cardRef = useRef<HTMLDivElement>(null);
  const thisChartData = safeGet(props, "data.trendChart.data.data[0].values", []);
  const { failure, pending } = props;
  const { bigNumber, vCompare, progress, isPositive, isHidden } = safeGet(props, "data", {});
  const [cardWidth, setCardWidth] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);
  const clickCard = useCallback(
    (ev: React.MouseEvent) => {
      if (ev) {
        const metricId = ev.currentTarget.getAttribute("data-item");
        props.showDetails(metricId, safeGet(props, "data.label", ""));
      }
    },
    [props.showDetails]
  );

  const barsConfig = useMemo(
    () => [
      { height: "1.872rem", width: "5.52rem", repeatBar: 1 },
      {
        height: "1.24rem",
        width: "4.16rem",
        repeatBar: 1,
        style: { marginTop: "6%" },
      },
    ],
    []
  );

  const dataPoints = useCallback(
    () => getTimeRoundOffData(thisChartData, GRANULARITY_MAPPING[props.selectedTab]),
    [thisChartData]
  )();

  useEffect(() => {
    setCardWidth(safeGet(cardRef, "current.clientWidth", 0));
    setCardHeight(safeGet(cardRef, "current.clientHeight", 0));
  }, []);

  const getPodNumberFromPodString = (brokenPodString: string) => {
    if (!brokenPodString) {
      return 0; // Return a default value if the input string is empty or undefined
    }

    const brokenPodStringArray = brokenPodString.split(" ");
    if (brokenPodStringArray.length === 0 || isNaN(parseBigNumber(brokenPodStringArray[0]))) {
      return 0; // Return a default value if the array is empty or the first element is not a number
    }

    return parseInt(String(parseBigNumber(brokenPodStringArray[0])), 10);
  };

  return (
    <main className={styles["overview-card"]} onClick={clickCard} data-item={safeGet(props, "data.id")}>
      <div className={styles["ovcard-grid-container"]} ref={cardRef}>
        {/* Left Column */}
        <div className={styles["ovcard-item1"]}>
          <div className={styles["ovcard-item1-head"]}>
            {props.data.bigNumber ? (
              props.data.label.toUpperCase()
            ) : (
              <Shimmer
                barsConfig={[
                  {
                    height: "2rem",
                    width: "13rem",
                    repeatBar: 1,
                    skimEffectColor: "white",
                    // elementSize: 'MEDIUM',
                    backgroundColor: "#d1d0cf",
                  },
                ]}
              />
            )}
          </div>
          <div>
            {safeGet(props, "data.lastUpdatedTimeStamp", "N/A") !== "N/A" ? (
              <div className={styles["last-updated"]}>
                Updated at {safeGet(props, "data.lastUpdatedTimeStamp", "N/A")}
              </div>
            ) : (
              <Shimmer
                barsConfig={[
                  {
                    height: "1rem",
                    width: "8rem",
                    repeatBar: 1,
                    skimEffectColor: "white",
                    backgroundColor: "#d1d0cf",
                  },
                ]}
              />
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className={styles["ovcard-item4"]}>
          {devLogging("metric id in overview ", props.data)}

          <div
            className={`${styles["chart-number"]} ${getChartNumberStyle(
              props.data.isPositive,
              parseBigNumber(props.data.bigNumber),
              parseBigNumber(String(props.data.performanceChartIds[1])),
              styles
            )}`}
          >
            {props.data.bigNumber ? (
              props.data.bigNumber
            ) : (
              <Shimmer
                barsConfig={[
                  {
                    height: "1.5rem",
                    width: "6rem",
                    repeatBar: 1,
                    skimEffectColor: "white",
                    backgroundColor: "#d1d0cf",
                  },
                ]}
              />
            )}
          </div>

          {devLogging("get selected Tab in overview card ", props.selectedTab)}
          {/* Don't Show broken pods for  */}
          {props.brokenPodsVisibleTabs?.includes(props.selectedTab) && (
            <div
              className={`${styles["chart-progress"]} ${
                getPodNumberFromPodString(props.data.vCompare) > 0 ? styles["down"] : styles["up"]
              }`}
            >
              {props.data.bigNumber ? (
                getPodNumberFromPodString(props.data.vCompare) + " Pods"
              ) : (
                <Spin indicator={<LoadingOutlined spin />} size="small" />
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};
OverviewCard.whyDidYouRender = false;

export default React.memo(withRouter(OverviewCard));
